import { useState } from 'react';
import { useMachine, type AppEvent } from '../../state/machine.ts';
import { LiveCounter } from './LiveCounter.tsx';
import s from './Landing.module.css';

type View = 'main' | 'trainSolo' | 'faceScans' | 'countChallenges';

const FACE_SCANS: ReadonlyArray<{ event: AppEvent; label: string }> = [
  { event: 'SOLO_SCAN',                label: 'face scan' },
  { event: 'SOLO_SMILE_SCAN',          label: 'smile scan' },
  { event: 'SOLO_FROWN_SCAN',          label: 'frown scan' },
  { event: 'SOLO_ANGER_SCAN',          label: 'anger scan' },
  { event: 'SOLO_SURPRISE_SCAN',       label: 'surprise scan' },
  { event: 'SOLO_DISGUST_SCAN',        label: 'disgust scan' },
  { event: 'SOLO_RESTING_FACE_SCAN',   label: 'neutral face' },
  { event: 'SOLO_STILLNESS_CHALLENGE', label: 'stillness' },
];

const COUNT_CHALLENGES: ReadonlyArray<{ event: AppEvent; label: string }> = [
  { event: 'SOLO_BLINK_CHALLENGE',     label: 'blink' },
  { event: 'SOLO_SHAKE_CHALLENGE',     label: 'shake' },
  { event: 'SOLO_NOD_CHALLENGE',       label: 'nod' },
  { event: 'SOLO_WINK_SCAN',           label: 'wink' },
];

export function Landing() {
  const send = useMachine((m) => m.send);
  const [view, setView] = useState<View>('main');

  if (view === 'trainSolo') {
    return (
      <main className={s.root}>
        <div className={s.scanlines} aria-hidden />
        <div className={s.grid}>
          <div className={s.eyebrow}>
            <span className={s.dot} aria-hidden /> train solo
          </div>
          <h2 className={s.subtitle}>pick your <span className={s.mog}>discipline</span></h2>
          <p className={s.tagline}>build muscle memory before you queue up.</p>
          <div className={s.ctaPair}>
            <button type="button" className={s.cta} onClick={() => setView('faceScans')}>
              face scans
            </button>
            <button type="button" className={s.cta} onClick={() => setView('countChallenges')}>
              count challenges
            </button>
          </div>
          <button type="button" className={s.menuBack} onClick={() => setView('main')}>
            ← back
          </button>
        </div>
      </main>
    );
  }

  if (view === 'faceScans' || view === 'countChallenges') {
    const isFace = view === 'faceScans';
    const items = isFace ? FACE_SCANS : COUNT_CHALLENGES;
    return (
      <main className={s.root}>
        <div className={s.scanlines} aria-hidden />
        <div className={s.grid}>
          <div className={s.eyebrow}>
            <span className={s.dot} aria-hidden /> {isFace ? 'face scans' : 'count challenges'}
          </div>
          <h2 className={s.subtitle}>
            {isFace ? <>quality <span className={s.mog}>metric</span></> : <>how <span className={s.mog}>many</span></>}
          </h2>
          <p className={s.tagline}>
            {isFace ? 'score your expression — peak detection across a 10-second window.' : 'how many can you pull off in 10 seconds?'}
          </p>
          <div className={s.soloRow}>
            {items.map((it) => (
              <button key={it.event} type="button" className={s.solo} onClick={() => send(it.event)}>
                {it.label}
              </button>
            ))}
          </div>
          <button type="button" className={s.menuBack} onClick={() => setView('trainSolo')}>
            ← back
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className={s.root}>
      <div className={s.scanlines} aria-hidden />
      <div className={s.grid}>
        <div className={s.eyebrow}>
          <span className={s.dot} aria-hidden /> live arena
        </div>
        <h1 className={s.title}>
          who <span className={s.mog}>mogs</span> who?
        </h1>
        <p className={s.tagline}>
          10-second 1v1 face-off. real cameras, real verdicts, ranked Elo.
        </p>
        <button type="button" className={s.cta} onClick={() => send('ENTER')}>
          enter arena
        </button>
        <button type="button" className={s.ctaOutline} onClick={() => setView('trainSolo')}>
          train solo
        </button>
        <LiveCounter />
        <p className={s.fineprint}>18+ &middot; biometric processing &middot; opt-in required</p>
      </div>
    </main>
  );
}
